import { useRef, useState } from "react";

const VIEWPORT_MARGIN = 8;

const findScrollableAncestor = (element) => {
  for (let parent = element.parentElement; parent; parent = parent.parentElement) {
    const { overflowY } = getComputedStyle(parent);
    if (overflowY === "auto" || overflowY === "scroll" || overflowY === "hidden") {
      return parent;
    }
  }
  return null;
};

const Tooltip = ({
  content,
  children,
  className = "",
  bubbleClassName = "whitespace-nowrap",
  distanceClassName = { up: "bottom-8", down: "top-8" },
  role,
  "aria-label": ariaLabel,
  "data-testid": dataTestId = "tooltip",
}) => {
  const anchorRef = useRef(null);
  const bubbleRef = useRef(null);
  const [direction, setDirection] = useState("down");
  const [horizontalOffset, setHorizontalOffset] = useState(0);

  const handlePosition = () => {
    if (!anchorRef.current || !bubbleRef.current) return;

    const anchorRect = anchorRef.current.getBoundingClientRect();
    const bubbleRect = bubbleRef.current.getBoundingClientRect();
    const clipper = findScrollableAncestor(anchorRef.current);
    const clipperRect = clipper?.getBoundingClientRect();
    const topEdge = clipperRect ? Math.max(clipperRect.top, 0) : 0;
    const bottomEdge = clipperRect
      ? Math.min(clipperRect.bottom, window.innerHeight)
      : window.innerHeight;
    const spaceAbove = anchorRect.top - topEdge;
    const spaceBelow = bottomEdge - anchorRect.bottom;

    setDirection(
      spaceBelow < bubbleRect.height + 12 && spaceAbove > spaceBelow ? "up" : "down"
    );

    const leftEdge = clipperRect ? Math.max(clipperRect.left, 0) : 0;
    const rightEdge = clipperRect
      ? Math.min(clipperRect.right, window.innerWidth)
      : window.innerWidth;
    const idealLeft = anchorRect.left + anchorRect.width / 2 - bubbleRect.width / 2;
    const minLeft = leftEdge + VIEWPORT_MARGIN;
    const maxLeft = rightEdge - bubbleRect.width - VIEWPORT_MARGIN;
    const clampedLeft = Math.min(Math.max(idealLeft, minLeft), maxLeft);
    setHorizontalOffset(clampedLeft - idealLeft);
  };

  return (
    <div
      ref={anchorRef}
      className={`group/tooltip relative ${className}`}
      role={role}
      aria-label={ariaLabel}
      data-testid={dataTestId}
      onMouseEnter={handlePosition}
    >
      {children}
      <span
        ref={bubbleRef}
        aria-hidden="true"
        className={`tooltip bg-black rounded-md text-white text-xs px-2 py-1 absolute left-1/2 z-10 invisible opacity-0 group-hover/tooltip:visible group-hover/tooltip:opacity-100 ${direction === "up" ? distanceClassName.up : distanceClassName.down} ${bubbleClassName}`}
        style={{ transform: `translateX(calc(-50% + ${horizontalOffset}px))` }}
        data-testid={`${dataTestId}-tooltip`}
      >
        {content}
      </span>
    </div>
  );
};

export default Tooltip;
